import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";
import { getStaffProvisioningEnvironment } from "../config/staffProvisioningEnvironment";
import { readPage } from "./DeviceDiagnosticsControls";
import "./styles/HardwareConnectionControls.css";

const WINDOW_MS = 120000, POLL_MS = 3000;
const ACTIONS = ["printer_status", "reconnect_printer"];
const STATES = { ready: "Ready", paper_empty: "Paper empty", cover_open: "Cover open", printer_error: "Printer error",
	unreachable: "Unreachable", busy: "Busy", restart_required: "Restart required", unsupported: "Unsupported", unknown: "Unknown" };
const REASONS = [...Object.keys(STATES).filter(state => state !== "ready"), "timeout", "connection_failed", "sdk_error",
	"invalid_printer_status", "unsupported_model", "reconnect_failed"];
const record = value => value !== null && typeof value === "object" && !Array.isArray(value);
const hash = value => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
const uuid = value => typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value);
const timestamp = value => Number.isSafeInteger(value) && value >= 0 && value <= 8640000000000000;
const ip = value => typeof value === "string" && /^\d{1,3}(\.\d{1,3}){3}$/.test(value) && value.split(".").every(part => Number(part) <= 255);
const keyFor = (kind, scope) => `scerv-hardware-${kind}-v1:${encodeURIComponent(JSON.stringify(scope))}`;
const storage = { getItem: key => window.localStorage.getItem(key), setItem: (key, value) => window.localStorage.setItem(key, value),
	removeItem: key => window.localStorage.removeItem(key) };
const errorCode = error => String(error?.code || "").replace(/^functions\//, "");
const createRequestId = () => {
	if (!window.crypto?.getRandomValues) throw new Error("Secure request identity unavailable");
	const bytes = window.crypto.getRandomValues(new Uint8Array(16));
	bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
	const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export function readHardwareDiagnostic(value, command, previous = null) {
	const invalid = () => { throw new Error("Invalid hardware diagnostic response"); };
	if (!record(value) || value.requestId !== command.requestId || value.action !== command.action || value.deviceId !== command.deviceId ||
		!record(value.printer) || value.printer.interfaceType !== "Lan" || !ip(value.printer.identifier) ||
		!["V-WRP2", "TSP100IV"].includes(value.printer.model) || !["queued", "running", "completed", "expired"].includes(value.status) ||
		!timestamp(value.createdAtMs) || !timestamp(value.expiresAtMs) || value.expiresAtMs !== value.createdAtMs + WINDOW_MS ||
		value.source !== "device_reported" || value.physicalPrintVerified !== false) invalid();
	if (previous && (previous.createdAtMs !== value.createdAtMs || previous.expiresAtMs !== value.expiresAtMs ||
		previous.printer.identifier !== value.printer.identifier || previous.printer.model !== value.printer.model ||
		(previous.status === "running" && value.status === "queued"))) invalid();
	let result = null;
	if (value.status === "completed") {
		const item = value.result;
		if (!timestamp(value.completedAtMs) || value.completedAtMs < value.createdAtMs || value.completedAtMs >= value.expiresAtMs ||
			!record(item) || !Object.hasOwn(STATES, item.state) || !(item.reason === null || REASONS.includes(item.reason)) ||
			!timestamp(item.checkedAtMs) || !Number.isSafeInteger(item.durationMs) || item.durationMs < 0 || item.durationMs > WINDOW_MS) invalid();
		result = { state: item.state, reason: item.reason, checkedAtMs: item.checkedAtMs, durationMs: item.durationMs };
	} else if (value.completedAtMs !== null || value.result !== null) invalid();
	return { requestId: value.requestId, action: value.action, deviceId: value.deviceId,
		printer: { interfaceType: "Lan", identifier: value.printer.identifier, model: value.printer.model }, status: value.status,
		createdAtMs: value.createdAtMs, expiresAtMs: value.expiresAtMs, completedAtMs: value.completedAtMs, result,
		source: "device_reported", physicalPrintVerified: false };
}

// Never discard ambiguous outcomes. Only a validated terminal server response retires an intent.
export function hardwareDiagnosticStore(target, scope) {
	const key = keyFor("diagnostic", scope);
	const validate = saved => {
		const command = saved?.command;
		if (saved?.version !== 1 || JSON.stringify(saved.scope) !== JSON.stringify(scope) || !timestamp(saved.savedAtMs) ||
			!record(command) || Object.keys(command).sort().join(",") !== "action,deviceId,firebaseProject,requestId,restaurantId" ||
			command.firebaseProject !== scope.firebaseProject || command.restaurantId !== scope.restaurantId ||
			command.deviceId !== scope.deviceId || !hash(command.deviceId) || !uuid(command.requestId) || !ACTIONS.includes(command.action)) {
			throw new Error("Stored diagnostic requires review");
		}
		return saved;
	};
	const read = () => { const raw = target.getItem(key); return raw === null ? null : validate(JSON.parse(raw)); };
	const ensure = saved => {
		validate(saved);
		const existing = read();
		if (existing && JSON.stringify(existing) !== JSON.stringify(saved)) throw new Error("Diagnostic intent changed");
		const raw = JSON.stringify(saved);
		if (!existing) target.setItem(key, raw);
		if (target.getItem(key) !== raw) throw new Error("Diagnostic intent was not saved");
		return saved;
	};
	const remove = saved => {
		if (JSON.stringify(read()) !== JSON.stringify(saved)) throw new Error("Diagnostic intent changed");
		target.removeItem(key);
		if (target.getItem(key) !== null) throw new Error("Diagnostic receipt was not saved");
	};
	return { read, ensure, remove };
}

function DeviceConnection({ scope, user, client, authClient, environment, selection, eligible }) {
	const [attempt, setAttempt] = useState(null), [diagnostic, setDiagnostic] = useState(null);
	const [busy, setBusy] = useState(false), [message, setMessage] = useState(""), [error, setError] = useState("");
	const [storageProblem, setStorageProblem] = useState(false);
	const store = useMemo(() => hardwareDiagnosticStore(storage, scope), [scope]);
	const canSubmit = useRef(eligible); canSubmit.current = eligible;
	const alive = useRef(false), revision = useRef({ value: 0 }), pending = useRef(false), latest = useRef(null);
	const pollTimer = useRef(null), deadlineTimer = useRef(null);
	const current = useCallback(ticket => alive.current && ticket === revision.current.value && selection.current === scope.deviceId &&
		auth === authClient && authClient.currentUser === user && functions === client && client.app.options.projectId === scope.firebaseProject &&
		(!authClient.app || authClient.app.options.projectId === scope.firebaseProject) && getStaffProvisioningEnvironment() === environment,
	[scope, selection, authClient, user, client, environment]);
	const stopTimers = useCallback(() => { clearTimeout(pollTimer.current); clearTimeout(deadlineTimer.current); }, []);
	const operate = useCallback(async (mode, action) => {
		if (pending.current || !current(revision.current.value)) return;
		if ((mode === "new" || mode === "retry") && !canSubmit.current) return;
		const ticket = ++revision.current.value;
		stopTimers(); pending.current = true; setBusy(true); setError(""); setMessage("");
		let saved;
		try {
			saved = store.read();
			if (!saved) {
				if (mode !== "new" || !ACTIONS.includes(action)) throw new Error("No saved diagnostic");
				saved = store.ensure({ version: 1, scope, savedAtMs: Date.now(), command: { firebaseProject: scope.firebaseProject,
					restaurantId: scope.restaurantId, deviceId: scope.deviceId, requestId: createRequestId(), action } });
			} else if (mode === "new") mode = "read";
			setAttempt(saved); setStorageProblem(false);
		} catch {
			setStorageProblem(true); setError("Saved request unavailable. Support review is required before another request.");
			pending.current = false; setBusy(false); return;
		}
		const deadline = saved.savedAtMs > Date.now() ? Date.now() : saved.savedAtMs + WINDOW_MS;
		// A separate deadline also bounds a callable that never settles. The durable intent survives it.
		deadlineTimer.current = setTimeout(() => {
			if (!current(ticket)) return;
			revision.current.value++; stopTimers(); pending.current = false; setBusy(false);
			setMessage("Waiting ended. Device may be offline; request outcome is unconfirmed.");
		}, mode === "new" ? Math.max(1, deadline - Date.now()) : deadline > Date.now() ? Math.min(15000, deadline - Date.now()) : 15000);
		const command = saved.command;
		if (latest.current?.requestId !== command.requestId) { latest.current = null; setDiagnostic(null); }
		const readCommand = { firebaseProject: command.firebaseProject, restaurantId: command.restaurantId, deviceId: command.deviceId, requestId: command.requestId };
		const call = async (endpoint, payload) => (await httpsCallable(client, endpoint, { timeout: 15000 })(payload)).data;
		try {
			let value;
			if (mode === "new") value = await call("requestScervHardwareDiagnostic", command);
			else {
				try { value = await call("getScervHardwareDiagnostic", readCommand); }
				catch (readError) {
					if (!current(ticket)) return;
					if (mode !== "retry" || errorCode(readError) !== "not-found") throw readError;
					if (!canSubmit.current) throw new Error("Enrollment changed");
					store.ensure(saved);
					value = await call("requestScervHardwareDiagnostic", command);
				}
			}
			if (!current(ticket)) return;
			const result = readHardwareDiagnostic(value, command, latest.current);
			latest.current = result; setDiagnostic(result);
			if (["completed", "expired"].includes(result.status)) {
				try { store.remove(saved); setAttempt(null); }
				catch { setStorageProblem(true); setError("Result received, but saved request could not be cleared. Support review is required."); }
			} else if (Date.now() < Math.min(deadline, result.expiresAtMs)) {
				pollTimer.current = setTimeout(() => { if (current(ticket)) operate("read"); }, Math.min(POLL_MS, deadline - Date.now()));
			} else setMessage("Waiting ended. Device may be offline; request outcome is unconfirmed.");
		} catch (failure) {
			if (!current(ticket)) return;
			const code = errorCode(failure);
			setError(["permission-denied", "unauthenticated"].includes(code) ? "Support access changed. Sign in with an authorized support account." :
				code === "not-found" ? "Saved request not found. Retry the same request to confirm submission." :
				["already-exists", "failed-precondition", "invalid-argument"].includes(code) ? "Device, printer configuration, or saved request requires review. The original request is retained." :
				"Connection unavailable or response unconfirmed. The saved request is retained.");
		} finally {
			if (current(ticket)) { clearTimeout(deadlineTimer.current); pending.current = false; setBusy(false); }
		}
	}, [current, scope, store, stopTimers, client]);
	useEffect(() => {
		const generation = revision.current;
		alive.current = true;
		try { const saved = store.read(); if (saved) { setAttempt(saved); operate("read"); } }
		catch { setStorageProblem(true); setError("Saved request unavailable. Support review is required before another request."); }
		return () => { alive.current = false; generation.value++; pending.current = false; stopTimers(); };
	}, [store, operate, stopTimers]);
	const waiting = attempt && diagnostic && ["queued", "running"].includes(diagnostic.status);
	return <div className="hardware-connection-result">
		<div className="hardware-connection-actions">
			<button type="button" disabled={!eligible || busy || !!attempt || storageProblem} onClick={() => operate("new", "printer_status")}>Check printer</button>
			<button type="button" disabled={!eligible || busy || !!attempt || storageProblem} onClick={() => operate("new", "reconnect_printer")}>Reconnect printer</button>
		</div>
		{error && <p role="alert">{error}</p>}
		<p role="status">{message || (busy ? "Checking saved request..." : diagnostic?.status === "expired" ? "Request expired. No connection result received." :
			waiting ? diagnostic.status === "running" ? "Device is checking the printer..." : "Waiting for device. It may be offline." :
			diagnostic?.result ? `Printer: ${STATES[diagnostic.result.state]}` : attempt ? "Request outcome unconfirmed." : "Printer status: Unknown")}</p>
		{diagnostic && <dl>
			<dt>Evidence</dt><dd>Device-reported status</dd>
			<dt>Saved printer</dt><dd>{diagnostic.printer.model} / {diagnostic.printer.identifier} (LAN)</dd>
			<dt>Check time</dt><dd>{diagnostic.result ? new Date(diagnostic.result.checkedAtMs).toLocaleString() : "Not received"}</dd>
			{diagnostic.result?.reason && <><dt>Reason</dt><dd>{diagnostic.result.reason.replace(/_/g, " ")}</dd></>}
			<dt>Request expires</dt><dd>{new Date(diagnostic.expiresAtMs).toLocaleString()}</dd>
		</dl>}
		{diagnostic && <p className="hardware-connection-meta">Physical print: Not verified</p>}
		<p className="hardware-connection-meta">Physical drawer state: Unknown</p>
		{attempt && <>
			<p className="hardware-connection-meta">{attempt.command.action === "printer_status" ? "Printer check" : "Printer reconnect"} request: {attempt.command.requestId}</p>
			<div className="hardware-connection-actions">
				<button type="button" disabled={busy || storageProblem} onClick={() => operate("read")}>Check saved request</button>
				<button type="button" disabled={!eligible || busy || storageProblem} onClick={() => operate("retry")}>Retry same request</button>
			</div>
		</>}
	</div>;
}

function ScopedConnections({ restaurantId, environment, projectId, user, client, authClient }) {
	const scope = useMemo(() => ({ firebaseProject: projectId, restaurantId, actorUid: user.uid, environment }), [projectId, restaurantId, user.uid, environment]);
	const selectionKey = keyFor("selection", scope);
	const [selected, setSelected] = useState(() => { try { const saved = storage.getItem(selectionKey); return hash(saved) ? saved : ""; } catch { return ""; } });
	const selection = useRef(selected);
	const [devices, setDevices] = useState([]), [nextCursor, setNextCursor] = useState(null);
	const [loading, setLoading] = useState(false), [loaded, setLoaded] = useState(false), [error, setError] = useState("");
	const alive = useRef(false), revision = useRef({ value: 0 }), pending = useRef(false);
	const headingId = useId(), selectId = useId();
	const deviceScope = useMemo(() => ({ ...scope, deviceId: selected }), [scope, selected]);
	const current = useCallback(ticket => alive.current && ticket === revision.current.value && auth === authClient && authClient.currentUser === user &&
		functions === client && client.app.options.projectId === projectId && (!authClient.app || authClient.app.options.projectId === projectId) &&
		getStaffProvisioningEnvironment() === environment, [authClient, user, client, projectId, environment]);
	const refresh = useCallback(async (cursor = null) => {
		if (pending.current || !current(revision.current.value)) return;
		const ticket = ++revision.current.value; pending.current = true; setLoading(true); setError("");
		try {
			const response = await httpsCallable(client, "getScervStaffDeviceDiagnostics")({ firebaseProject: projectId, restaurantId, ...(cursor ? { cursor } : {}) });
			if (!current(ticket)) return;
			const page = readPage(response.data, restaurantId, projectId, cursor);
			setDevices(previous => cursor ? [...previous, ...page.devices] : page.devices); setNextCursor(page.nextCursor); setLoaded(true);
		} catch { if (current(ticket)) setError("Enrolled devices are unavailable. Access or enrollment could not be confirmed."); }
		finally { if (current(ticket)) { pending.current = false; setLoading(false); } }
	}, [client, current, projectId, restaurantId]);
	useEffect(() => {
		const generation = revision.current;
		alive.current = true; refresh();
		return () => { alive.current = false; generation.value++; pending.current = false; };
	}, [refresh]);
	const enrolled = devices.filter(device => device.enrollmentStatus === "enrolled");
	const selectedDevice = enrolled.find(device => device.deviceId === selected);
	return <section className="hardware-connection" aria-labelledby={headingId}>
		<header><h2 id={headingId}>Printer connection</h2>
			<button type="button" className="hardware-connection-icon" aria-label="Refresh enrolled devices" title="Refresh enrolled devices" disabled={loading} onClick={() => refresh()}>&#8635;</button>
		</header>
		<p className="hardware-connection-meta">{environment} / {restaurantId}</p>
		<label htmlFor={selectId}>Enrolled device</label>
		<select id={selectId} value={selected} onChange={event => {
			if (!current(revision.current.value)) return;
			selection.current = event.target.value; setSelected(event.target.value);
			try { storage.setItem(selectionKey, event.target.value); } catch { /* Request persistence is checked separately before submission. */ }
		}}>
			<option value="">Select a device</option>
			{selected && !selectedDevice && <option value={selected} disabled>Saved device {selected.slice(0, 12)} (not in loaded enrolled devices)</option>}
			{enrolled.map(device => <option key={device.deviceId} value={device.deviceId}>{device.label} ({device.deviceId.slice(0, 12)})</option>)}
		</select>
		{error && <p role="alert">{error}</p>}
		{loading && <p role="status">Loading enrolled devices...</p>}
		{loaded && !enrolled.length && <p>No enrolled devices on this page.</p>}
		{nextCursor && <button type="button" disabled={loading} onClick={() => refresh(nextCursor)}>Load more devices</button>}
		{selected ? <DeviceConnection key={selected} scope={deviceScope} environment={environment} user={user} client={client}
			authClient={authClient} selection={selection} eligible={!!selectedDevice && !loading && !error} /> :
			<div className="hardware-connection-actions"><button type="button" disabled>Check printer</button><button type="button" disabled>Reconnect printer</button></div>}
	</section>;
}

export default function HardwareConnectionControls({ restaurantId }) {
	const [user, loading, error] = useAuthState(auth);
	const actor = useRef({ user: null, client: null, authClient: null, revision: 0 });
	if (actor.current.user !== user || actor.current.client !== functions || actor.current.authClient !== auth) {
		actor.current = { user, client: functions, authClient: auth, revision: actor.current.revision + 1 };
	}
	const environment = getStaffProvisioningEnvironment(), projectId = functions.app.options.projectId;
	if (!restaurantId || !environment) return null;
	if (loading) return <p role="status">Checking support access...</p>;
	if (error || !user || auth.currentUser !== user || (auth.app && auth.app.options.projectId !== projectId)) return <p role="alert">Sign in with an authorized support account.</p>;
	return <ScopedConnections key={JSON.stringify([restaurantId, environment, projectId, user.uid, actor.current.revision])}
		restaurantId={restaurantId} environment={environment} projectId={projectId} user={user} client={functions} authClient={auth} />;
}
